import debounce from '../util/debounce';
import throttle from '../util/throttle';
import {register} from '../vendor/theme-scripts/theme-sections';
import outerWidth from '../features/outer-width-margins';
import navigation from '../features/navigation';
import {collapsible} from '../features/collapsible';

const selectors = {
  body: 'body',
  bodyWrap: '[data-body-wrap]',
  mainContent: '.main-content',
  navMain: '[data-nav-main]',
  navItem: '.menu > .menu-item',
  headerRow: '.container > .row',
  headerIcons: '[data-header-icons]',
  dataHeaderStyle: '[data-header-style="transparent"]',
  logoImage: '[data-logo-image]',
  logoImageWidth: 'data-width',
  logoText: '[data-logo-text]',
  hamburgerIcon: '[data-hamburger-icon]',
  hamburgerMenuScrollable: '[data-hamburger-scrollable]',
  siteAlert: '[data-site-alert]',
  shopBar: '[data-shop-bar]',
  cartClose: '[data-cart-close]',
  dataTransparent: 'data-transparent',
};

const classes = {
  shopifyHeaderFixed: 'shopify-section-header--fixed',
  headerFull: 'header--full',
  headerScrolled: 'header--has-scrolled',
  headerStandard: 'header--is-standard',
  headerHamburger: 'header--is-hamburger',
  headerSearch: 'header--search-visible',
  headerTransparent: 'header--transparent',
  headerLogoCenterCenter: 'header--logo_center_links_center',
  headerLogoLeftCenter: 'header--logo_left_links_center',
  headerLogoCenterLeft: 'header--logo_center_links_left',
  headerLogoLeftLeft: 'header--logo_left_links_left',
  hasTransparentHeader: 'has-transparent-header',
  navVisible: 'nav--is-visible',
  cartVisible: 'cart--is-visible',
  open: 'open',
};

let sections = {};

class Header {
  constructor(el) {
    this.header = el;
    this.headerContainer = this.header.parentNode;
    this.body = document.querySelector(selectors.body);
    this.bodyWrap = document.querySelector(selectors.bodyWrap);
    this.scrollEvent = throttle(() => this.stickyHeaderState(), 50);
    this.resizeEvent = debounce(() => this.checkNavigationOverlapping(), 250);
    this.checkTransparentHeaderEvent = (e) => this.checkTransparentHeader(e);
    this.checkNavigationOverlappingEvent = this.checkNavigationOverlapping();

    this.init();
  }

  init() {
    this.resetHeader();
    this.checkTransparentHeader();
    this.checkNavigationOverlapping();
    this.stickyHeaderState();

    window.addEventListener('load', this.checkNavigationOverlappingEvent);
    window.addEventListener('scroll', this.scrollEvent);
    window.addEventListener('resize', this.resizeEvent);

    document.addEventListener('theme:header:update', this.checkTransparentHeaderEvent);
  }

  stickyHeaderState() {
    const scrolled = window.scrollY;
    let contentTop = 0;

    if (this.bodyWrap !== null) {
      contentTop = this.bodyWrap.offsetTop;
    }

    if (scrolled > contentTop) {
      this.headerContainer.classList.add(classes.shopifyHeaderFixed);
      this.header.classList.add(classes.headerScrolled);
      this.header.classList.remove(classes.headerTransparent);
    } else {
      this.headerContainer.classList.remove(classes.shopifyHeaderFixed);
      this.header.classList.remove(classes.headerScrolled);

      if (this.isHeaderTransparent()) {
        this.header.classList.add(classes.headerTransparent);
      }
    }
  }

  checkNavigationOverlapping() {
    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const isDesktop = windowWidth > theme.sizes.large;
    const classesToRemove = [classes.headerStandard, classes.headerSearch];

    this.header.classList.remove(classes.headerHamburger);
    this.header.classList.add(classes.headerStandard);

    if (this.getNavigationOverlapping() || !isDesktop) {
      this.header.classList.remove(...classesToRemove);
      this.header.classList.add(classes.headerHamburger);
    }
  }

  checkTransparentHeader() {
    if (this.isHeaderTransparent()) {
      this.body.classList.add(classes.hasTransparentHeader);
      this.header.classList.add(classes.headerTransparent);
    } else {
      this.body.classList.remove(classes.hasTransparentHeader);
      this.header.classList.remove(classes.headerTransparent);
    }
  }

  getNavigationOverlapping() {
    const headerRowWidth = this.header.querySelector(selectors.headerRow).offsetWidth;
    const navMenuWidth = this.getMenuItemsWidth();
    const headerClasses = this.header.classList;
    const isNavCentered = headerClasses.contains(classes.headerLogoCenterCenter) || headerClasses.contains(classes.headerLogoLeftCenter);
    const isNavLeftLogoCentered = headerClasses.contains(classes.headerLogoCenterLeft) || headerClasses.contains(classes.headerLogoLeftLeft);
    const additionalSpace = 40; // Additional spacing from margins
    let isNavigationOverlapping = false;
    let headerIconsWidth = this.header.querySelector(selectors.headerIcons).offsetWidth;
    let logoWidth = this.getLogoWidth();

    if (isNavCentered) {
      logoWidth = logoWidth < headerIconsWidth ? headerIconsWidth : logoWidth;
      logoWidth *= 2;
      headerIconsWidth = 0;
    }

    isNavigationOverlapping = parseInt(headerRowWidth) < parseInt(navMenuWidth + logoWidth + headerIconsWidth + additionalSpace);

    if (isNavLeftLogoCentered && logoWidth) {
      isNavigationOverlapping = parseInt((headerRowWidth - logoWidth) / 2) < parseInt(navMenuWidth);
    }

    return isNavigationOverlapping;
  }

  getLogoWidth() {
    const logoImage = this.header.querySelector(selectors.logoImage);
    const logoText = this.header.querySelector(selectors.logoText);
    let logoWidth = 0;

    if (logoImage !== null) {
      logoWidth = parseInt(logoImage.getAttribute(selectors.logoImageWidth));
    }

    if (logoText !== null) {
      logoWidth += logoText.offsetWidth;
    }

    return logoWidth;
  }

  getMenuItemsWidth() {
    let itemsWidth = 0;
    const navStandard = this.header.querySelector(selectors.navMain);

    const menuItems = navStandard.querySelectorAll(selectors.navItem);
    menuItems.forEach((menuItem) => {
      itemsWidth += outerWidth(menuItem);
    });

    return itemsWidth;
  }

  isHeaderTransparent() {
    const firstSection = document.querySelector(selectors.mainContent).firstElementChild;
    const firstSectionClass = firstSection.classList.contains(classes.headerFull);
    const firstSectionStyle = firstSection.querySelector(selectors.dataHeaderStyle) !== null;
    const transparentHeader = this.header.getAttribute(selectors.dataTransparent) === 'true';

    const headerFull = firstSectionClass || firstSectionStyle;
    const headerTransparent = transparentHeader && headerFull;

    return headerTransparent;
  }

  resetHeader() {
    const siteAlert = document.querySelector(selectors.siteAlert);
    const shopBar = document.querySelector(selectors.shopBar);
    const hamburger = this.header.querySelector(selectors.hamburgerIcon);
    const transformElements = [this.header, siteAlert, this.bodyWrap, shopBar];
    const body = document.body;

    body.classList.remove(classes.navVisible);
    this.bodyWrap.removeAttribute('style');
    transformElements.forEach((element) => {
      if (element !== null) {
        element.style.transform = 'none';
      }
    });
    hamburger.classList.remove(classes.open);

    if (body.classList.contains(classes.cartVisible)) {
      document.querySelector(selectors.cartClose).click();
    }

    // Unlock page scroll
    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
  }

  unload() {
    window.removeEventListener('load', this.checkNavigationOverlappingEvent);
    window.removeEventListener('scroll', this.scrollEvent);
    window.removeEventListener('resize', this.resizeEvent);

    document.removeEventListener('theme:header:update', this.checkTransparentHeaderEvent);
  }

  onselect() {
    this.resetHeader();
    this.init();
  }
}

const header = {
  onLoad() {
    sections = new Header(this.container);
  },
  onUnload() {
    if (typeof sections.unload === 'function') {
      sections.unload();
    }
  },
  onSelect() {
    if (typeof sections.onselect === 'function') {
      sections.onselect();
    }
  },
};

export {Header as default};

register('header', [header, navigation, collapsible]);
